export {};

interface MapsLocation {
    lat: string;
    lng: string;
}

const mapsConsentCategory = 'marketing';

declare global {
    interface Window {
        maps?: MapsLocation;
        mapsLocation?: MapsLocation;
        mapsKey?: string;
        cmplz_has_consent?: (category: string) => boolean;
    }
}

let googleMapsApiPromise: Promise<void> | null = null;

function loadGoogleMapsApi(): Promise<void> {
    if ((window as any).google?.maps?.importLibrary) {
        return Promise.resolve();
    }

    const key = window.mapsKey;

    if (!key) {
        return Promise.reject(new Error('Google Maps API key is not defined'));
    }

    if (!googleMapsApiPromise) {
        googleMapsApiPromise = new Promise((resolve, reject) => {
            const existingScript = document.querySelector<HTMLScriptElement>('script[src*="maps.googleapis.com/maps/api/js"]');

            if (existingScript) {
                existingScript.addEventListener('load', () => resolve(), {once: true});
                existingScript.addEventListener('error', () => reject(new Error('Google Maps API could not load')), {once: true});
                return;
            }

            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&v=weekly`;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Google Maps API could not load'));
            document.head.append(script);
        });
    }

    return googleMapsApiPromise;
}

function hasMapsConsent(): boolean {
    if (typeof window.cmplz_has_consent !== 'function') {
        return true;
    }

    return window.cmplz_has_consent(mapsConsentCategory) === true;
}

async function initMap(element: HTMLElement): Promise<void> {
    if (element.dataset.mapInitialized === 'true') {
        return;
    }

    element.dataset.mapInitialized = 'true';

    try {
        const location = window.maps;

        if (!location) {
            console.error('mapsLocation is not defined on window');
            return;
        }

        const lat = parseFloat(location.lat);
        const lng = parseFloat(location.lng);

        if (isNaN(lat) || isNaN(lng)) {
            console.error('Invalid coordinates:', location);
            return;
        }

        await loadGoogleMapsApi();

        const [{Map, RenderingType}, {AdvancedMarkerElement}] = await Promise.all([
            await (window as any).google.maps.importLibrary('maps') as google.maps.MapsLibrary,
            await (window as any).google.maps.importLibrary('marker') as google.maps.MarkerLibrary
        ]);

        const map = new Map(element, {
            center: {lat, lng},
            zoom: 16,
            renderingType: RenderingType.VECTOR,
            mapId: 'aac2541512470fc7a001090f',
            mapTypeControl: false,
            zoomControl: false,
            streetViewControl: false,
            scaleControl: false,
            fullscreenControl: false,
            rotateControl: false,
            cameraControl: false,
            panControl: false,
        });

        new AdvancedMarkerElement({
            map: map,
            position: {lat, lng},
        });
    } catch (error) {
        delete element.dataset.mapInitialized;
        console.error('Failed to initialize Google Map', error);
    }
}

function initConsentedMaps(): void {
    if (!hasMapsConsent()) {
        return;
    }

    document.querySelectorAll<HTMLElement>('.google-maps').forEach(map => {
        void initMap(map);
    });
}

window.addEventListener('DOMContentLoaded', () => {
    initConsentedMaps();
});

document.addEventListener('cmplz_enable_category', (event) => {
    const detail = (event as CustomEvent<{ category?: string }>).detail;

    if (detail?.category === mapsConsentCategory) {
        initConsentedMaps();
    }
});

document.addEventListener('cmplz_cookie_warning_loaded', () => {
    initConsentedMaps();
});

document.addEventListener('cmplz_run_after_all_scripts', () => {
    initConsentedMaps();
});
